import { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { TbCircleDottedLetterN } from 'react-icons/tb';
import FormInput from '../../../../components/FormInput';
import AccordionIcon from './AccordionIcon';
import RunEntry from '../../../RunList/RunEntry/RunEntry';
import Button from '../../../../components/Button';
import {
  HiNoSymbol,
  HiOutlineNoSymbol,
  HiOutlineLockClosed,
  HiOutlineLockOpen,
  HiOutlinePlayCircle,
  HiOutlineTrash,
  HiOutlinePlusCircle,
  HiChevronUp,
  HiChevronDown,
  HiOutlinePencilSquare,
} from 'react-icons/hi2';
import { dragFromAverages, dragFromDeltas } from '../../../../utils/drag';
import { numberToLetters } from '../../../../utils/letterNumber';
import { DISC_LETTER_NUMBER } from '../../../../constants/apiConstants';
import GridContent from 'components/GridContent';
import { useFormContext } from 'react-hook-form';
import { useUpdateConfigId } from '../../../../hooks/useConfigs';
import toast from 'react-hot-toast';
import { EditOperationMode, isOperationLocked } from './editOperationUtils';

const CALIBRATION_COPY_FIELDS = [
  'wheel_circumference', 'power_meter_slope', 'crr', 'cp_corr', 'yaw_offset', 'combined_mass',
  'cda_ref', 'xw_slope', 'xw_para_slope', 'xw_stall_angle', 'xw_stall_slope', 'xw_wheel_compensation',
  'chest_slope', 'chest_ref', 'head_slope', 'head_ref', 'airspeed_slope',
  'speed_sensor_pos', 'air_temp_offset', 'crr_temp_compensation_dict',
  'alt_power', 'alt_spd', 'gps_speed', 'combine_power', 'wheel_magnet_count',
  'autocal_params', 'autocal_skip', 'settings_collection',
  'gates_id',
];

interface ConfigEntryProps {
  index: number;
  isEdit: boolean;
  originalConfig: any;
  showAllRuns: boolean;
  updateNextConfigId: any;
  plannedRunConfigIds: any[];
  setPlannedRunConfigIds: any;
  nextRunIndex: number;
  activeRunId: string | null;
  setActiveRunId: (id: string | null) => void;
  invalidateSession: any;
  reorderConfigLetters: () => void;
  handleReprocessConfig: (configId: string, shouldReprocess: boolean) => void;
  reprocessingConfigId: string | null;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  onSwapConfig?: (index: number, direction: 'up' | 'down') => void;
  changedConfigIds?: Set<string>;
  setChangedConfigIds?: (ids: Set<string>) => void;
  hasDuplicateDescription?: boolean;
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
}

export default function ConfigEntry(props: ConfigEntryProps): ReactElement {
  const {
    index,
    isEdit,
    originalConfig,
    showAllRuns,
    updateNextConfigId,
    plannedRunConfigIds,
    setPlannedRunConfigIds,
    nextRunIndex,
    activeRunId,
    setActiveRunId,
    invalidateSession,
    handleReprocessConfig,
    reprocessingConfigId,
    reorderConfigLetters,
    configMovesToSave,
    onSwapConfig,
    changedConfigIds,
    setChangedConfigIds,
    hasDuplicateDescription,
    editOpMode,
    setEditOpMode,
    onReprocessRun,
  } = props;

  const { watch, setValue, getValues } = useFormContext();
  const config = watch(`configs.${index}`);
  const updateConfig = useUpdateConfigId(config.id);
  const navigate = useNavigate();

  // Get sessionId from outlet context
  const { sessionId } = useOutletContext<{
    sessionId: string;
    session: any;
    isSessionFetching: boolean;
    isSessionPending: boolean;
    isNew: boolean;
  }>();

  // Active parameters that should be locked to manual
  const activeParameters = [
    'power_meter_slope',
    'cp_corr',
    'yaw_offset',
    'wheel_circumference',
    'cda_ref',
    'chest_ref',
    'head_ref',
    'chest_slope',
    'head_slope',
    'airspeed_ref',
    'airspeed_slope',
    'xw_wheel_compensation',
    'xw_para_slope',
    'xw_stall_slope',
    'xw_stall_angle',
    'crr',
    'combined_mass',
    'elevation_difference',
  ];

  const configs = watch('configs');

  if (!config) {
    return <></>;
  }

  const isAutocalLocked = config.autocal_params
    && !Object.values(config.autocal_params).some(Boolean);

  const [showRuns, setShowRuns] = useState(true);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  const [autocalBranch, setAutocalBranch] = useState(() => {
    const params = config.autocal_params || {};
    return {
      cp_corr: !!params.cp_corr,
      yaw_offset: !!params.yaw_offset,
      wheel_circumference: !!params.wheel_circumference,
      manual_change: false,
    };
  });
  const configsById = (configs || []).reduce((map: any, config: any) => {
    if (config?.id) {
      map[config.id] = config;
    }
    return map;
  }, {});
  const parent = config.baseline_id && configsById[config.baseline_id];
  const parentDrag = parent && dragFromAverages(parent);
  const drag = dragFromAverages(config);
  const delta = dragFromDeltas(config);
  const isNextRun = plannedRunConfigIds[0] === config.id;
  const hasPlannedRun = plannedRunConfigIds.includes(config.id);

  const validConfigs = configs.filter(
    (c: any) => c && c?.letter_number !== undefined && c.letter_number < config.letter_number,
  );

  // Display letter should always be based on the current index position for valid configs
  // Find the index among non-deleted, non-disc configs
  const displayIndex =
    configs
      .slice(0, index + 1)
      .filter((c: any) => !c.isDeleted && c.letter_number !== undefined && c.letter_number < DISC_LETTER_NUMBER)
      .length - 1;

  const letter =
    config.letter_number === DISC_LETTER_NUMBER ? numberToLetters(config.letter_number) : numberToLetters(displayIndex);

  // Use useFieldArray directly in this component to avoid hook order issues
  const runs = watch(`configs.${index}.runs`);

  useEffect(() => {
    setShowRuns(showAllRuns);
  }, [showAllRuns]);

  const originalLetter =
    originalConfig?.letter_number !== undefined ? numberToLetters(originalConfig.letter_number) : '';

  function addPlannedRun() {
    setPlannedRunConfigIds([...plannedRunConfigIds, config.id]);
  }

  function toggleAutocalSkip() {
    const newValue = !config.autocal_skip;
    setValue(`configs.${index}.autocal_skip`, newValue, { shouldDirty: true });
  }

  async function toggleAutocalLock() {
    try {
      // First, lock all parameters to manual by copying inherited/automatic values
      const configData = { ...config };

      activeParameters.forEach((field) => {
        // Get the current value from parameters_inherited if it exists
        const inheritedData = config.parameters_inherited?.[field];
        const inheritedValue = inheritedData?.[0];

        if (inheritedValue !== null && inheritedValue !== undefined) {
          // Set the inherited value as the actual value
          configData[field] = inheritedValue;
          setValue(`configs.${index}.${field}`, inheritedValue, { shouldDirty: true });
        }
      });

      // Clear autocal and set parameters_manual to include all active parameters
      configData.autocal_params = {};
      configData.parameters_manual = activeParameters;

      // Handle gates specifically for elevation_difference
      if (configData.gates) {
        configData.gates.elevation_manual = true;
      }

      // Clear fields that should not be sent in update payload
      delete configData.parameters_inherited;
      delete configData.gates_automatic;

      // Update the configuration via API
      await updateConfig.mutateAsync(configData);

      // Update the form to reflect the locked state
      setValue(`configs.${index}.autocal_params`, {}, { shouldDirty: true });
      setValue(`configs.${index}.parameters_manual`, activeParameters, { shouldDirty: true });

      toast.success(`Configuration ${numberToLetters(config.letter_number)} locked - all parameters set to manual`);
    } catch (error) {
      console.error('Failed to lock configuration:', error);
      toast.error('Failed to lock configuration');
    }
  }

  function updateAutocalBranch(field: string, value: boolean) {
    const newAutocalBranch = { ...autocalBranch, [field]: value };
    setAutocalBranch(newAutocalBranch);

    const autocalParameters = ['cp_corr', 'yaw_offset', 'wheel_circumference'];
    const autocalObj: Record<string, boolean> = {};

    autocalParameters.forEach((param) => {
      autocalObj[param] = !!newAutocalBranch[param];
      if (!newAutocalBranch[param] && originalConfig?.autocal_params?.[param]) {
        setValue(`configs.${index}.${param}`, null, { shouldDirty: true });
      }
    });

    setValue(`configs.${index}.autocal_params`, autocalObj, { shouldDirty: true });
  }

  function handleToggleDestroy() {
    if (isOperationLocked(editOpMode, 'delete')) return;
    setEditOpMode?.('delete');
    const currentValue = !!getValues(`configs.${index}.isDeleted`);
    setValue(`configs.${index}.isDeleted`, !currentValue, { shouldDirty: true });
    reorderConfigLetters();
  }

  function handleBranchConfig() {
    // Navigate to CalibrationView for this config within the session context
    navigate(`/sessions/${sessionId}/calibrations/${config.id}`);
  }

  function handleSwapToBaseline() {
    if (isOperationLocked(editOpMode, 'reorder')) return;

    const confirmed = window.confirm(
      'This will promote this config to baseline. Manual calibration values on the current baseline may be overwritten. Continue?',
    );
    if (!confirmed) return;

    const currentConfigs = getValues('configs');
    if (!currentConfigs || currentConfigs.length < 2) return;

    const oldBaseline = currentConfigs[0];
    const targetIndex = currentConfigs.findIndex((c: any) => c.id === config.id);
    if (targetIndex <= 0) return;

    // Copy calibration fields from old baseline to this config
    for (const field of CALIBRATION_COPY_FIELDS) {
      if (oldBaseline[field] !== undefined) {
        setValue(`configs.${targetIndex}.${field}`, oldBaseline[field], { shouldDirty: true });
      }
    }

    // Build the new configs array: move target to position 0
    const newConfigs = [...currentConfigs];
    const [target] = newConfigs.splice(targetIndex, 1);

    // Update baseline references
    target.baseline_id = null;
    oldBaseline.baseline_id = target.id;

    // Also update any other configs that had the old baseline as parent to point to the new baseline
    for (const c of newConfigs) {
      if (c.id !== oldBaseline.id && c.baseline_id === oldBaseline.id) {
        c.baseline_id = target.id;
      }
    }

    newConfigs.unshift(target);
    setValue('configs', newConfigs, { shouldDirty: true });

    // Mark both configs as changed
    const newChangedIds = new Set(changedConfigIds);
    newChangedIds.add(target.id);
    newChangedIds.add(oldBaseline.id);
    setChangedConfigIds?.(newChangedIds);

    reorderConfigLetters();
    setEditOpMode?.('reorder');
  }

  const headerClasses = [
    'cr-col-span-7',
    'cr-grid',
    'cr-grid-cols-subgrid',
    'cr-rounded-t-lg',
    'cr-border-2',
    'cr-border-solid',
    'cr-border-cirrusBlue-100',
    'cr-bg-cirrusBlue-50',
    showRuns ? 'cr-border-b-0' : 'cr-rounded-lg',
    config.isDeleted ? 'cr-opacity-50' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const descriptionClasses = [
    'cr-text-left',
    'cr-text-lg',
    'cr-font-bold',
    'cr-whitespace-nowrap',
    'cr-overflow-hidden',
    'cr-text-ellipsis',
    'cr-flex',
    'cr-items-center',
    'cr-gap-2',
    'cr-min-w-0',
    hasDuplicateDescription && !config.isDeleted ? 'cr-text-red-700' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="cr-col-span-7 cr-mb-4 cr-grid cr-grid-cols-subgrid cr-shadow-lg">
      <div className={headerClasses}>
        {/* First Row: Chevron and Description */}
        <div className="cr-flex cr-items-center cr-justify-center cr-pt-2 cr-pb-1">
          <div
            onClick={() => {
              setShowRuns(!showRuns);
            }}
            className="cr-cursor-pointer"
          >
            <AccordionIcon show={showRuns} />
          </div>
        </div>
        <div className="cr-col-span-6 cr-flex cr-min-w-0 cr-items-center cr-justify-between cr-px-2 cr-pt-2 cr-pb-1">
          {isEdit && config.letter_number !== DISC_LETTER_NUMBER ? (
            <div className="cr-flex cr-w-full cr-min-w-0 cr-items-center cr-gap-2">
              <div className="cr-flex-1 cr-min-w-0">
                <FormInput
                  fieldName={`configs.${index}.description`}
                  rows={1}
                  required="Description is required"
                  customClass={`!cr-w-full cr-text-left cr-text-lg cr-font-bold ${
                    hasDuplicateDescription && !config.isDeleted ? '!cr-border-red-500 !cr-bg-red-50' : ''
                  }`}
                />
              </div>
              {hasDuplicateDescription && !config.isDeleted && (
                <div
                  className="cr-whitespace-nowrap cr-text-[17px] cr-font-medium cr-text-red-500 md:cr-text-sm"
                  title="Duplicate configuration name - this will cause server issues"
                >
                  ⚠ Duplicate
                </div>
              )}
            </div>
          ) : (
            <div className={descriptionClasses}>
              {config.description}
              {hasDuplicateDescription && !config.isDeleted && (
                <span
                  className="cr-text-[17px] cr-font-medium cr-text-red-500 md:cr-text-sm"
                  title="Duplicate configuration name - this will cause server issues"
                >
                  ⚠ Duplicate
                </span>
              )}
            </div>
          )}
          <div className="cr-ml-1 cr-flex cr-flex-shrink-0 cr-items-center md:cr-ml-2">
            {hasPlannedRun &&
              (isEdit ? (
                <label className="cr-flex cr-items-center cr-gap-1">
                  <input
                    className="cr-size-5"
                    type="radio"
                    name="next"
                    value={config.id}
                    checked={isNextRun}
                    onChange={(e) => updateNextConfigId(e.target.value)}
                  />
                  <span className="cr-hidden md:cr-inline">Next</span>
                </label>
              ) : (
                isNextRun && <TbCircleDottedLetterN size={28} />
              ))}
          </div>
        </div>

        {/* Second Row: Config Letter and Drag */}
        <div className="cr-border-t-2 cr-border-cirrusBlue-100"></div>
        <div className="cr-flex cr-min-w-0 cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-pl-0">
          <div className="cr-flex cr-items-center cr-gap-2">
            <span className="cr-text-lg cr-font-bold">{letter}</span>

            {changedConfigIds?.has(config.id) && originalLetter && originalLetter !== letter && (
              <span className="cr-text-[17px] cr-text-[#03365D]/60 md:cr-text-sm">({originalLetter})</span>
            )}
            {/* Up/Down arrows for reordering */}
            {isEdit && onSwapConfig && config.letter_number < DISC_LETTER_NUMBER && (
              <div className="cr-flex cr-flex-col cr-gap-1">
                {/* Show up arrow if not baseline and not previous letter is baseline */}
                {index > 0 &&
                  (() => {
                    const previousConfig = configs[index - 1];
                    return previousConfig
                      && previousConfig.baseline_id !== config.id
                      && config.baseline_id !== previousConfig.id;
                  })() && (
                    <button
                      type="button"
                      onClick={() => onSwapConfig(index, 'up')}
                      className={`cr-p-0 cr-text-gray-600 hover:cr-text-gray-800 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                      disabled={isOperationLocked(editOpMode, 'reorder')}
                      title={isOperationLocked(editOpMode, 'reorder') ? 'Save pending changes first' : 'Move up'}
                    >
                      <HiChevronUp className="cr-h-4 cr-w-4" />
                    </button>
                  )}
                {/* Show down arrow if not parent of next configuration */}
                {index < configs.length - 1 &&
                  (() => {
                    const nextConfig = configs[index + 1];
                    return (
                      nextConfig &&
                      nextConfig.baseline_id !== config.id &&
                      nextConfig.letter_number < DISC_LETTER_NUMBER
                    );
                  })() && (
                    <button
                      type="button"
                      onClick={() => onSwapConfig(index, 'down')}
                      className={`cr-p-0 cr-text-gray-600 hover:cr-text-gray-800 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                      disabled={isOperationLocked(editOpMode, 'reorder')}
                      title={isOperationLocked(editOpMode, 'reorder') ? 'Save pending changes first' : 'Move down'}
                    >
                      <HiChevronDown className="cr-h-4 cr-w-4" />
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
        <div className="cr-flex cr-min-w-0 cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-pl-0">
          {isEdit && index > 0 && config.letter_number < DISC_LETTER_NUMBER ? (
            <FormInput
              fieldName={`configs.${index}.baseline_id`}
              customClass="!cr-w-12 cr-text-lg cr-font-semibold cr-text-center"
              options={validConfigs}
              optionLabel="letter"
            />
          ) : parent && parent.letter_number !== undefined ? (
            <div className="cr-text-lg cr-font-semibold">{numberToLetters(parent.letter_number)}</div>
          ) : (
            <div></div>
          )}
        </div>
        <div className="cr-relative cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1">
          <div className="cr-flex cr-items-center cr-gap-1">
            <div className="cr-text-center cr-text-lg cr-font-semibold">{drag && drag.toFixed(3)}</div>
          </div>
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1 ">
          {delta && (
            <span className={`cr-text-lg cr-font-semibold ${delta < 0 ? 'cr-text-blue-500' : 'cr-text-red'}`}>
              {delta > 0 ? '+' : ''}
              {delta.toFixed(3)}
            </span>
          )}
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-border-t-2 cr-border-cirrusBlue-100 cr-py-1 ">
          <div className="cr-flex cr-gap-1">
            {config.autocal_skip && <HiOutlineNoSymbol className="cr-size-7" />}
            {isAutocalLocked && <HiOutlineLockClosed className="cr-size-7" />}
            {isEdit &&
              configs.length > 1 &&
              config.letter_number !== 0 &&
              config.letter_number !== DISC_LETTER_NUMBER && (
                <>
                  <Button
                    className={`cr-ml-2 cr-rounded cr-bg-red-100 cr-py-2 cr-text-red-700 hover:cr-bg-red-200 md:cr-w-40 ${isOperationLocked(editOpMode, 'delete') ? 'cr-opacity-40' : ''}`}
                    onClick={handleToggleDestroy}
                    disabled={isOperationLocked(editOpMode, 'delete')}
                    icon={<HiOutlineTrash className="cr-size-5" />}
                  >
                    <span className="cr-hidden md:cr-inline">{config.isDeleted ? 'un-Delete' : 'Delete'}</span>
                  </Button>
                </>
              )}
          </div>
        </div>
      </div>

      {showRuns && (
        <GridContent spanCount={7}>
          {runs.map((run: any) => {
            return (
              <RunEntry
                key={run.id}
                run={run}
                isNextRun={false}
                config={config}
                baselineDrag={parentDrag}
                isEdit={isEdit}
                activeRunId={activeRunId}
                setActiveRunId={setActiveRunId}
                configs={configs}
                invalidateRuns={invalidateSession}
                hideColumns={{ rider: true, session: true, config: true }}
                context="sessions"
                configMovesToSave={configMovesToSave}
                editOpMode={editOpMode}
                setEditOpMode={setEditOpMode}
                onReprocessRun={onReprocessRun}
              />
            );
          })}
          {plannedRunConfigIds.map((configId: any, index: number) => {
            return (
              configId === config.id && (
                <RunEntry
                  key={index}
                  config={config}
                  run={{ run_number: nextRunIndex + index + 1, id: null }}
                  isNextRun={index === 0}
                  isEdit={isEdit}
                  onDelete={() => {
                    const newPlannedRunConfigIds = [...plannedRunConfigIds];
                    newPlannedRunConfigIds.splice(index, 1);
                    setPlannedRunConfigIds(newPlannedRunConfigIds);
                  }}
                  activeRunId={activeRunId}
                  setActiveRunId={setActiveRunId}
                  configs={configs}
                  invalidateRuns={invalidateSession}
                  hideColumns={{ rider: true, session: true, config: true }}
                  context="sessions"
                />
              )
            );
          })}
          {isEdit && config.letter_number < DISC_LETTER_NUMBER && (
            <>
              <div className="cr-col-span-5 cr-flex cr-items-center cr-justify-between cr-py-1">
                <div className="cr-flex cr-gap-2 cr-font-main">
                  <Button
                    className="cr-bg-cirrusBlue-50"
                    onClick={addPlannedRun}
                    icon={<HiOutlinePlusCircle className={'cr-size-5'} />}
                  >
                    Add run
                  </Button>
                </div>
                <div className="cr-py-1">
                  <div
                    className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2 cr-font-main cr-text-[17px] cr-font-semibold md:cr-text-sm"
                    onClick={() => {
                      setShowAdvancedConfig(!showAdvancedConfig);
                    }}
                  >
                    <AccordionIcon show={showAdvancedConfig} />
                    <span>Advanced Configuration</span>
                  </div>
                </div>
              </div>

              {showAdvancedConfig && (
                <div className="cr-col-span-5 cr-py-1">
                  <div className="cr-mt-3 cr-space-y-3">
                    <div className="cr-flex cr-gap-2">
                      <Button
                        className="cr-bg-cirrusBlue-50"
                        onClick={toggleAutocalSkip}
                        icon={
                          config.autocal_skip ? (
                            <HiOutlinePlayCircle className={'cr-size-7'} />
                          ) : (
                            <HiNoSymbol className={'cr-size-7'} />
                          )
                        }
                      >
                        {config.autocal_skip ? 'Use' : 'Do Not Use'}
                      </Button>
                      <Button
                        className="cr-bg-cirrusBlue-50"
                        onClick={toggleAutocalLock}
                        icon={
                          isAutocalLocked ? (
                            <HiOutlineLockOpen className={'cr-size-7'} />
                          ) : (
                            <HiOutlineLockClosed className={'cr-size-7'} />
                          )
                        }
                      >
                        {isAutocalLocked ? 'Unlock' : 'Lock'}
                      </Button>
                      {config.letter_number !== 0 && (
                        <Button
                          className={`cr-bg-amber-50 cr-text-amber-800 hover:cr-bg-amber-100 ${isOperationLocked(editOpMode, 'reorder') ? 'cr-opacity-40' : ''}`}
                          onClick={handleSwapToBaseline}
                          disabled={isOperationLocked(editOpMode, 'reorder')}
                          icon={<HiChevronUp className={'cr-size-5'} />}
                        >
                          Swap to Baseline
                        </Button>
                      )}
                    </div>

                    <div className="cr-flex cr-items-center cr-gap-4 cr-font-main cr-text-[17px] md:cr-text-sm">
                      <span>Update calibration autocal params:</span>
                      <div className="cr-flex cr-gap-4">
                        <label className="cr-flex cr-items-center cr-gap-1">
                          <input
                            type="checkbox"
                            className="cr-size-4"
                            checked={autocalBranch.wheel_circumference}
                            onChange={(e) => updateAutocalBranch('wheel_circumference', e.target.checked)}
                          />
                          <span>Wheel Circumference</span>
                        </label>
                        <label className="cr-flex cr-items-center cr-gap-1">
                          <input
                            type="checkbox"
                            className="cr-size-4"
                            checked={autocalBranch.yaw_offset}
                            onChange={(e) => updateAutocalBranch('yaw_offset', e.target.checked)}
                          />
                          <span>Yaw Offset</span>
                        </label>
                        <label className="cr-flex cr-items-center cr-gap-1">
                          <input
                            type="checkbox"
                            className="cr-size-4"
                            checked={autocalBranch.cp_corr}
                            onChange={(e) => updateAutocalBranch('cp_corr', e.target.checked)}
                          />
                          <span>CP Corr</span>
                        </label>
                      </div>
                    </div>

                    {/* Branch button */}
                    <div className="cr-flex cr-gap-2">
                      <Button
                        className="cr-bg-cirrusBlue-50"
                        onClick={handleBranchConfig}
                        icon={<HiOutlinePencilSquare className={'cr-size-5'} />}
                      >
                        Create / Edit Calibration
                      </Button>
                    </div>

                    {/* Reprocess runs checkbox */}
                    {config.id && runs && runs.length > 0 && (
                      <div className="cr-rounded-md cr-bg-gray-50 cr-p-3">
                        <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-3">
                          <input
                            type="checkbox"
                            checked={reprocessingConfigId === config.id}
                            onChange={(e) => handleReprocessConfig(config.id, e.target.checked)}
                            className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-blue-600 focus:cr-ring-blue-500"
                          />
                          <span className="cr-text-[17px] cr-font-medium cr-text-[#03365D] md:cr-text-sm">
                            Reprocess runs for this config
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </GridContent>
      )}
    </div>
  );
}
